"use client";

import OfficeLocation from "@/common/OfficeLocation";
import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Select from "@/designs/Select";
import Table from "@/designs/Table";
import {
  useGetBookingSpacesQuery,
  useGetSpaceCategoriesQuery,
  useGetSpacesQuery,
} from "@/graphql/generated/schema";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import React, { useContext, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Calendar, Search, SlidersHorizontal } from "lucide-react";
import NewBooking from "./NewBooking";
import DateRangePicker from "@/designs/DateRangePicker";

const BookingSpaces = () => {
  const { theme, selectedLocation } = useContext(CMSModal);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: spacesData } = useGetSpacesQuery({
    variables: { location: selectedLocation! },
    skip: !selectedLocation,
  });

  const { data: categoriesData } = useGetSpaceCategoriesQuery({
    variables: { location: selectedLocation! },
    skip: !selectedLocation,
  });

  const spaceOptions = useMemo(
    () => [
      { value: "", label: "All Spaces" },
      ...(spacesData?.getSpaces ?? []).map((s: any) => ({
        value: s?._id ?? "",
        label: s?.name ?? "",
      })),
    ],
    [spacesData?.getSpaces],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "", label: "All Categories" },
      ...(categoriesData?.getSpaceCategories ?? []).map((c: any) => ({
        value: c?._id ?? "",
        label: c?.name ?? "",
      })),
    ],
    [categoriesData?.getSpaceCategories],
  );

  const { data, loading } = useGetBookingSpacesQuery({
    variables: {
      location: selectedLocation,
      start: startDate || undefined,
      end: endDate || undefined,
      space: selectedSpace || undefined,
    },
    pollInterval: 50000,
  });

  const rows = useMemo(() => {
    const list = (data?.getBookingSpaces ?? []) as any[];
    return list.filter((b) => {
      if (selectedCategory && b?.category?._id !== selectedCategory) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const employee =
        `${b?.employee?.firstName || ""} ${b?.employee?.lastName || ""}`.toLowerCase();
      const resource = String(b?.resource?.name || "").toLowerCase();
      return employee.includes(q) || resource.includes(q);
    });
  }, [data?.getBookingSpaces, search, selectedCategory]);

  const handleExport = () => {
    const headers = [
      "Employee",
      "Resource",
      "Category",
      "Start",
      "End",
      "Location",
      "Space",
    ];
    const csvRows = rows.map((b: any) => {
      const name =
        `${b?.employee?.firstName || ""} ${b?.employee?.lastName || ""}`.trim() ||
        "-";
      return [
        name,
        b?.resource?.name || "-",
        b?.category?.name || "-",
        b?.start ? moment(b.start).format("h:mma, MMM D YYYY") : "-",
        b?.end ? moment(b.end).format("h:mma, MMM D YYYY") : "-",
        b?.location?.name || "-",
        b?.space?.name || "-",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bookings-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      {
        id: "employee",
        header: "Employee",
        accessor: "employee",
        sortable: true,
        render: (_: any, row: any) => {
          const name =
            `${row?.employee?.firstName || ""} ${row?.employee?.lastName || ""}`.trim() ||
            "-";
          const initials = name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((p: string) => p[0]?.toUpperCase())
            .join("");
          return (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-700">
                {row?.employee.img ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={row?.employee?.img}
                    alt="profile"
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  initials
                )}
              </div>
              <div
                className={twMerge(
                  theme === "dark" ? "text-gray-100" : "text-gray-700",
                )}
              >
                {name}
              </div>
            </div>
          );
        },
      },
      {
        id: "resource",
        header: "Resource",
        accessor: "resource",
        sortable: true,
        render: (_: any, row: any) => row?.resource?.name || "-",
      },
      {
        id: "category",
        header: "Category",
        accessor: "category",
        sortable: true,
        render: (_: any, row: any) => row?.category?.name || "-",
      },
      {
        id: "start",
        header: "Start",
        accessor: "start",
        sortable: true,
        render: (value: any) =>
          value ? moment(value).format("h:mma, MMM D") : "-",
      },
      {
        id: "end",
        header: "End",
        accessor: "end",
        sortable: true,
        render: (value: any) =>
          value ? moment(value).format("h:mma, MMM D") : "-",
      },
      {
        id: "repeat",
        header: "Repeat",
        accessor: "_id",
        render: () => "-",
      },
      {
        id: "location",
        header: "Location",
        accessor: "location",
        sortable: true,
        render: (_: any, row: any) => row?.location?.name || "-",
      },
      {
        id: "space",
        header: "Space",
        accessor: "space",
        sortable: true,
        render: (_: any, row: any) => row?.space?.name || "-",
      },
    ],
    [theme],
  );

  return (
    <div
      className={twMerge(
        "p-4 lg:p-10 space-y-4 transition-colors rounded",
        theme === "dark" ? "bg-[#191919]" : "bg-white",
      )}
    >
      <div>
        {" "}
        <h1 className="text-3xl font-semibold text-gray-800">
          Bookings{" "}
          <span className="font-normal text-gray-700">
            at <OfficeLocation disabled />
          </span>
        </h1>
      </div>
      <div className="mt-6 flex flex-col xl:flex-row xl:items-center gap-4 justify-between">
        <div className="flex flex-col lg:flex-row gap-4 w-full">
          <div className="w-full lg:max-w-md">
            <Input
              placeholder="Search by employee or resource"
              icon={<Search size={18} />}
              onChange={(value) => setSearch(value)}
              value={search}
            />
          </div>

          <div className="w-full lg:w-auto lg:min-w-[348px]">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
              mode="both"
            />
          </div>

          <button
            className="text-[#099ed1] flex items-center gap-1 font-bold whitespace-nowrap"
            onClick={() => setIsFilterOpen((p) => !p)}
          >
            <SlidersHorizontal size={14} />
            Filters
          </button>
        </div>

        <div className="flex gap-3">
          <Button
            className="w-32 bg-white text-sky-500 border border-sky-500 hover:bg-sky-50"
            onClick={handleExport}
          >
            Export
          </Button>
          <Button className="w-36" onClick={() => setShowNew(true)}>
            New Booking
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.25, ease: "easeInOut" },
              opacity: { duration: 0.15, ease: "easeInOut" },
            }}
            className="overflow-hidden"
          >
            <div
              className={twMerge(
                "border p-4 flex flex-wrap gap-4 items-end",
                theme === "dark"
                  ? "bg-[#262727] border-gray-700"
                  : "bg-[#f5fbfd] border-[#c4e7f5]",
              )}
            >
              <Select
                label="Space"
                value={selectedSpace ?? ""}
                options={spaceOptions}
                onChange={(value) => setSelectedSpace(value || null)}
                className="w-48"
              />
              <Select
                label="Resource Category"
                value={selectedCategory ?? ""}
                options={categoryOptions}
                onChange={(value) => setSelectedCategory(value || null)}
                className="w-48"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6">
        <Table
          data={rows}
          columns={columns as any}
          loading={loading}
          emptyObject={{
            icon: <Calendar size={32} />,
            title: "There are no bookings",
            description: "Keep track of who’s got what, for when",
          }}
        />
      </div>

      <NewBooking isOpen={showNew} onClose={() => setShowNew(false)} />
    </div>
  );
};

export default BookingSpaces;
